import {
  useContext,
  useRef,
  createElement,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { Box, Button } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { ChatContext } from "../contexts/chat/context";
import { DemandWalkEditsContext } from "../contexts/demand_walk_edits";
import { PurchasingCalendarEditsContext } from "../contexts/purchasing_calendar_edits";
import { useModal } from "../contexts/modal/hooks";
import { ChatSessionWarningContent } from "../components/modals/chat_session_warning";
import { ChooseActionStep } from "../components/modals/force_user_edit_conclude/choose_action_step";
import { ChooseActionButtons } from "../components/modals/force_user_edit_conclude/choose_action_buttons";
import { NameRunStepContainer } from "../components/modals/force_user_edit_conclude";
import { SupplyWalkEditsContext } from "../contexts/supply_walk_edits/provider";

type NavigationGuard = {
  shouldBlock: boolean;
  blockingReason: string;
  handler: () => void;
};

export const useGuardedNavigation = (): {
  navigateWithGuards: (targetPath: string) => void;
  hasActiveBlocks: boolean;
  activeBlocks: NavigationGuard[];
} => {
  const { isConnected, chatSessionId, disconnectChat } =
    useContext(ChatContext);
  const {
    editMode: demandWalkEditMode,
    saveEditsAndTriggerRun: demandWalkSave,
    discardChanges: demandWalkDiscard,
  } = useContext(DemandWalkEditsContext);
  const {
    editMode: purchasingCalendarEditMode,
    saveEditsAndTriggerRun: purchasingCalendarSave,
    discardChanges: purchasingCalendarDiscard,
  } = useContext(PurchasingCalendarEditsContext);

  const editMode = demandWalkEditMode || purchasingCalendarEditMode;
  const saveEditsAndTriggerRun = purchasingCalendarEditMode
    ? purchasingCalendarSave
    : demandWalkSave;
  const discardChanges = purchasingCalendarEditMode
    ? purchasingCalendarDiscard
    : demandWalkDiscard;
  const { openModal, closeModal } = useModal();
  const navigate = useNavigate();
  const location = useLocation();
  const pendingTargetPathRef = useRef<string>("");
  const isBlockingNavigationRef = useRef(false);
  const supplyEdits = useContext(SupplyWalkEditsContext);

  const openForceUserEditModal = useCallback(
    (startAtStep: number = 0) => {
      const handleDiscard = () => {
        discardChanges();
        closeModal();
        // Navigate after discarding changes
        navigate(pendingTargetPathRef.current);
      };

      const handleTriggerRun = (runName: string) => {
        saveEditsAndTriggerRun(runName);
        closeModal();
        // Navigate after saving and triggering run
        navigate(pendingTargetPathRef.current);
      };

      openModal({
        disableStepViewer: true,
        maxWidth: "sm",
        currentStepIndex: startAtStep,
        steps: [
          {
            id: "step1",
            title: "Unsaved Changes",
            content: createElement(ChooseActionStep),
            customActions: createElement(ChooseActionButtons, {
              onDiscard: handleDiscard,
              onTriggerRun: handleTriggerRun,
            }),
          },
          {
            id: "step2",
            title: "Name Your Run",
            content: createElement(NameRunStepContainer, {
              onDiscard: handleDiscard,
              onTriggerRun: handleTriggerRun,
            }),
            disableActions: true,
          },
        ],
      });
    },
    [discardChanges, saveEditsAndTriggerRun, closeModal, navigate, openModal]
  );

  const openSupplyDiscardWarning = useCallback(() => {
    const handleDiscard = () => {
      supplyEdits?.discardChanges?.();
      closeModal();
      navigate(pendingTargetPathRef.current);
    };

    const handleReview = () => {
      closeModal();
    };

    openModal({
      disableStepViewer: true,
      maxWidth: "sm",
      steps: [
        {
          id: "supply_unsaved_changes",
          title: "Unsaved Changes",
          content: createElement(
            Box,
            { sx: {} },
            "Please approve your changes before navigating away or they will be lost."
          ),
          customActions: createElement(
            Box,
            { sx: { display: "flex", justifyContent: "flex-end", gap: 1 } },
            createElement(
              Button,
              { variant: "outlined", onClick: handleDiscard },
              "it's ok, discard changes"
            ),
            createElement(
              Button,
              { variant: "contained", color: "primary", onClick: handleReview },
              "ok, let me review"
            )
          ),
        },
      ],
    });
  }, [supplyEdits, closeModal, navigate, openModal]);

  const showChatSessionWarning = useCallback(() => {
    const handleConfirm = () => {
      disconnectChat();
      closeModal();
      // Proceed with navigation after chat is disconnected
      navigate(pendingTargetPathRef.current);
    };

    const handleCancel = () => {
      closeModal();
    };

    openModal({
      disableStepViewer: true,
      maxWidth: "sm",
      disableBackdropClick: true,
      disableEscapeKeyDown: true,
      steps: [
        {
          id: "chat_session_warning",
          title: "Active Chat Session",
          content: createElement(ChatSessionWarningContent, {
            onConfirm: handleConfirm,
            onCancel: handleCancel,
          }),
          disableActions: true,
        },
      ],
    });
  }, [disconnectChat, closeModal, navigate, openModal]);

  // Define all possible navigation guards with useMemo
  const guards: NavigationGuard[] = useMemo(() => {
    const guards = [
      // Supply unapproved edits guard - only when navigating away from supply walk editable page
      {
        shouldBlock: (() => {
          const isOnSupplyPage = location.pathname === "/po-inbox";
          const hasUnapproved = supplyEdits && supplyEdits.hasUnapprovedEdits();
          const shouldBlock = !!(isOnSupplyPage && hasUnapproved);

          return shouldBlock;
        })(),
        blockingReason: "supplyUnapprovedEdits",
        handler: () => openSupplyDiscardWarning(),
      },
      // Edit mode guard (highest priority)
      {
        shouldBlock: editMode,
        blockingReason: "editMode",
        handler: () => openForceUserEditModal(),
      },
      // Active chat session guard - only when navigating away from chat page
      {
        shouldBlock: !!(
          isConnected &&
          chatSessionId &&
          location.pathname === "/chat"
        ),
        blockingReason: "activeChat",
        handler: () => showChatSessionWarning(),
      },
    ];

    return guards;
  }, [
    editMode,
    isConnected,
    chatSessionId,
    location.pathname,
    openForceUserEditModal,
    showChatSessionWarning,
    supplyEdits,
    openSupplyDiscardWarning,
  ]);

  const navigateWithGuards = (targetPath: string) => {
    pendingTargetPathRef.current = targetPath;

    // Check guards dynamically instead of using cached shouldBlock values
    const activeGuard = guards.find((guard) => {
      if (guard.blockingReason === "supplyUnapprovedEdits") {
        const isOnSupplyPage = location.pathname === "/po-inbox";
        const hasUnapproved = supplyEdits && supplyEdits.hasUnapprovedEdits();
        const shouldBlock = !!(isOnSupplyPage && hasUnapproved);
        return shouldBlock;
      }
      return guard.shouldBlock;
    });

    if (activeGuard) {
      activeGuard.handler();
    } else {
      // No guards active, navigate normally
      navigate(targetPath);
    }
  };

  const activeGuards = guards.filter((guard) => guard.shouldBlock);

  // Modified handlers for browser navigation
  const showChatSessionWarningForBrowser = useCallback(() => {
    const handleConfirm = () => {
      disconnectChat();
      closeModal();
      isBlockingNavigationRef.current = false;
      // Allow browser to go back
      window.history.back();
    };

    const handleCancel = () => {
      closeModal();
      isBlockingNavigationRef.current = false;
    };

    openModal({
      disableStepViewer: true,
      maxWidth: "sm",
      disableBackdropClick: true,
      disableEscapeKeyDown: true,
      steps: [
        {
          id: "chat_session_warning_browser",
          title: "Active Chat Session",
          content: createElement(ChatSessionWarningContent, {
            onConfirm: handleConfirm,
            onCancel: handleCancel,
          }),
          disableActions: true,
        },
      ],
    });
  }, [disconnectChat, closeModal, openModal]);

  const openForceUserEditModalForBrowser = useCallback(
    (startAtStep: number = 0) => {
      const handleDiscard = () => {
        discardChanges();
        closeModal();
        isBlockingNavigationRef.current = false;
        // Allow browser to go back
        window.history.back();
      };

      const handleTriggerRun = (runName: string) => {
        saveEditsAndTriggerRun(runName);
        closeModal();
        isBlockingNavigationRef.current = false;
        // Allow browser to go back
        window.history.back();
      };

      openModal({
        disableStepViewer: true,
        maxWidth: "sm",
        currentStepIndex: startAtStep,
        steps: [
          {
            id: "step1",
            title: "Unsaved Changes",
            content: createElement(ChooseActionStep),
            customActions: createElement(ChooseActionButtons, {
              onDiscard: handleDiscard,
              onTriggerRun: handleTriggerRun,
            }),
          },
          {
            id: "step2",
            title: "Name Your Run",
            content: createElement(NameRunStepContainer, {
              onDiscard: handleDiscard,
              onTriggerRun: handleTriggerRun,
            }),
            disableActions: true,
          },
        ],
      });
    },
    [discardChanges, saveEditsAndTriggerRun, closeModal, openModal]
  );

  // Handle browser navigation events (back/forward)
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // Only show native browser dialog for page refresh/close, not navigation
      if (activeGuards.length > 0) {
        event.preventDefault();
        event.returnValue =
          "You have unsaved changes. Are you sure you want to leave?";
      }
    };

    const handlePopState = () => {
      // Browser back/forward navigation
      if (activeGuards.length > 0 && !isBlockingNavigationRef.current) {
        isBlockingNavigationRef.current = true;

        // Push current state back to prevent navigation
        window.history.pushState(null, "", window.location.href);

        // Show our custom modal instead
        if (supplyEdits?.hasUnapprovedEdits()) {
          // For browser back, show the same discard modal and then go back
          const handleDiscard = () => {
            supplyEdits?.discardChanges?.();
            closeModal();
            isBlockingNavigationRef.current = false;
            window.history.back();
          };
          const handleReview = () => {
            closeModal();
            isBlockingNavigationRef.current = false;
          };
          openModal({
            disableStepViewer: true,
            maxWidth: "sm",
            steps: [
              {
                id: "supply_unsaved_changes_browser",
                title: "Unsaved Changes",
                content: createElement(
                  Box,
                  { sx: {} },
                  "Please approve your changes before navigating away or they will be lost."
                ),
                customActions: createElement(
                  Box,
                  {
                    sx: { display: "flex", justifyContent: "flex-end", gap: 1 },
                  },
                  createElement(
                    Button,
                    { variant: "outlined", onClick: handleDiscard },
                    "it's ok, discard changes"
                  ),
                  createElement(
                    Button,
                    {
                      variant: "contained",
                      color: "primary",
                      onClick: handleReview,
                    },
                    "ok, let me review"
                  )
                ),
              },
            ],
          });
          return;
        }

        const activeGuard = guards.find((guard) => {
          if (guard.blockingReason === "supplyUnapprovedEdits") {
            const isOnSupplyPage = location.pathname === "/po-inbox";
            const hasUnapproved =
              supplyEdits && supplyEdits.hasUnapprovedEdits();
            const shouldBlock = !!(isOnSupplyPage && hasUnapproved);
            return shouldBlock;
          }
          return guard.shouldBlock;
        });
        if (activeGuard) {
          // Modify handlers to allow browser navigation after confirmation
          if (activeGuard.blockingReason === "activeChat") {
            showChatSessionWarningForBrowser();
          } else if (activeGuard.blockingReason === "editMode") {
            openForceUserEditModalForBrowser();
          }
        }
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);
    };
  }, [
    activeGuards.length,
    guards,
    showChatSessionWarningForBrowser,
    openForceUserEditModalForBrowser,
    openSupplyDiscardWarning,
    closeModal,
    openModal,
    supplyEdits,
  ]);

  return {
    navigateWithGuards,
    hasActiveBlocks: activeGuards.length > 0,
    activeBlocks: activeGuards,
  };
};
